import { inject, Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs';
import { formatToNgbDate } from 'src/app/common/app.helpers';
import { DateValidationService } from 'src/app/forms/fields/components/date-validation.service';
import { ZipCodeInfo } from '../interfaces/country-info.interface';
import { DependentDatePickerData, EnableIfContent, FieldAttribute } from '../interfaces/form-and-field.interface';
import { CountriesService } from '../services/countries.service';
import { ZipCodeService } from '../services/zip-code.service';
import { FieldCustomValidators } from './fields/field-custom.validators';
import { FormValidationService } from './form-validation.service';

@Injectable()
export class FormStateService {
  private readonly dependencyTypes: {
    [key: string]: (content: EnableIfContent) => void;
  } = {
    country_state_select: (content: EnableIfContent) => this.disableEnableIf(content),
    insured_characters: (content: EnableIfContent) => this.hideEnableIf(content),
    zip_code_zip_code: (content: EnableIfContent) => this.validatorsEnableIf(content),
    community_id_projects_search_select: (content: EnableIfContent) => this.postLinkedOwnerEnableIf(content),
    project_id_communities_search_select: (content: EnableIfContent) => this.postLinkedOwnerEnableIf(content),
    start_date_date: (content: EnableIfContent) => this.updateStartDateInput(content),
    end_date_date: (content: EnableIfContent) => this.updateEndDateInput(content),
  };

  private readonly allowedTypes: string[] = Object.keys(this.dependencyTypes);

  private formValidationService = inject(FormValidationService);
  private countriesService = inject(CountriesService);
  private zipCodeService = inject(ZipCodeService);
  private dateValidationService = inject(DateValidationService);

  public getControl(group: UntypedFormGroup, attribute: FieldAttribute): UntypedFormControl {
    return group.get(attribute.backendAttribute.name) as UntypedFormControl;
  }

  public getEnabledIfControl(group: UntypedFormGroup, attribute: FieldAttribute): UntypedFormControl {
    return group.get(attribute.backendAttribute.enabled_if.name) as UntypedFormControl;
  }

  public initControl(group: UntypedFormGroup, attribute: FieldAttribute): UntypedFormControl {
    const control: UntypedFormControl = this.getControl(group, attribute);
    this.checkDependency(group, attribute);

    if (attribute.disabled) {
      control.disable();
    }
    return control;
  }

  public validateAllFormFields(form: UntypedFormGroup): void {
    form.markAllAsTouched();
    form.markAsPristine();
    form.updateValueAndValidity();
  }

  public validateControl(control: UntypedFormControl): void {
    control.markAsTouched();
    control.markAsPristine();
    control.updateValueAndValidity();
  }

  public stopAllAction(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public onFormReset(form: UntypedFormGroup): void {
    form.markAsUntouched();
    form.setErrors(null);
  }

  private getDependencyType(attribute: FieldAttribute): string {
    return `${attribute.backendAttribute.enabled_if.name}_${attribute.backendAttribute.value_class_front}`;
  }

  private checkDependency(group: UntypedFormGroup, attribute: FieldAttribute): void {
    if (attribute.backendAttribute.enabled_if) {
      const type: string = this.getDependencyType(attribute);
      if (this.allowedTypes.includes(type)) {
        this.dependencyTypes[type]({
          control: this.getControl(group, attribute),
          enableIfControl: this.getEnabledIfControl(group, attribute),
          attribute,
        });
      }
    }
  }

  private postLinkedOwnerEnableIf(content: EnableIfContent): void {
    this.postLinkedOwnerConditionControlValue(content, content.enableIfControl.value as string);
    this.subscribeOnChanges(this.postLinkedOwnerConditionControlValue.bind(this), content);
  }

  private hideEnableIf(content: EnableIfContent): void {
    this.changeHideConditionControlValue(content, content.enableIfControl.value as string);
    this.subscribeOnChanges(this.changeHideConditionControlValue.bind(this), content);
  }

  private disableEnableIf(content: EnableIfContent): void {
    content.isEqual = true;
    disableEnableControl(content);
    this.subscribeOnChanges(disableEnableControl, content);
  }

  private validatorsEnableIf(content: EnableIfContent): void {
    content.isEqual = false;
    this.changeValidators(content);
    this.subscribeOnChanges(this.changeValidators.bind(this), content);
  }

  private subscribeOnChanges(
    method: (content: EnableIfContent, value: string) => void,
    content: EnableIfContent,
  ): void {
    content.enableIfControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((value: string) => method.call(this, content, value));
  }

  private postLinkedOwnerConditionControlValue(content: EnableIfContent, value: string): void {
    manageDisableEnableState(Boolean(value), content);
  }

  private changeHideConditionControlValue(content: EnableIfContent, value: string): void {
    const hidden: boolean = value !== content.attribute.backendAttribute.enabled_if?.value;

    manageDisableEnableState(hidden, content);
    content.attribute.hidden = hidden;

    if (hidden) {
      content.control.markAsUntouched();
    }
  }

  private changeValidators(content: EnableIfContent): void {
    const iso: string | null = this.countriesService.getCountryByName(content.enableIfControl.value as string);
    content.condition = Boolean(iso);
    disableEnableControl(content);
    if (iso) {
      const info: ZipCodeInfo = this.zipCodeService.getZipCodeInfo(iso);
      content.condition = Boolean(iso);
      disableEnableControl(content);

      if (info) {
        this.updateZipCodeValidator(content, info);
      }
    }
  }

  private updateZipCodeValidator(content: EnableIfContent, info: ZipCodeInfo): void {
    const baseValidator: ValidatorFn[] = [
      FieldCustomValidators.patternValidator(info.zipCodeFormat, {
        zip_code: true,
      }),
      Validators.minLength(content.attribute.backendAttribute.min_length),
    ];

    const validator: ValidatorFn[] = content.attribute.backendAttribute.required
      ? [Validators.required].concat(baseValidator)
      : baseValidator;

    this.formValidationService.changeValidators(content.control, validator);
    content.attribute.backendAttribute.placeholder = info.zipCodeSample;
  }

  private updateStartDateInput(content: EnableIfContent): void {
    this.updateMinDateBoundary(content, content.enableIfControl.value as DependentDatePickerData);
    this.subscribeOnChanges(this.updateMinDateBoundary.bind(this), content);
  }

  private updateEndDateInput(content: EnableIfContent): void {
    this.updateMaxDateBoundary(content, content.enableIfControl.value as DependentDatePickerData);
    this.subscribeOnChanges(this.updateMaxDateBoundary.bind(this), content);
  }

  private updateMinDateBoundary(content: EnableIfContent, value: string | DependentDatePickerData): void {
    if (typeof value === 'string') {
      content.control.setValue(
        {
          origin: content.control.value as DependentDatePickerData,
          minDate: formatToNgbDate(value),
          maxDate: this.dateValidationService.getMaxDate(content.attribute),
        },
        { onlySelf: true, emitEvent: true },
      );
    }
  }

  private updateMaxDateBoundary(content: EnableIfContent, value: string | DependentDatePickerData): void {
    if (typeof value === 'string') {
      content.control.setValue(
        {
          origin: content.control.value as DependentDatePickerData,
          minDate: this.dateValidationService.getMinDate(content.attribute),
          maxDate: formatToNgbDate(value),
        },
        { onlySelf: true, emitEvent: true },
      );
    }
  }
}

function manageDisableEnableState(condition: boolean, content: EnableIfContent): void {
  if (condition) {
    content.control.setValue('');
    content.control.disable({ onlySelf: true });
  } else {
    content.control.enable({ onlySelf: true });
  }
}

function disableEnableControl(content: EnableIfContent): void {
  manageDisableEnableState(
    content.isEqual
      ? content.enableIfControl.value !== content.attribute.backendAttribute.enabled_if.value
      : content.condition,
    content,
  );
}
